import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Pillar, PostCard } from '@/types'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { HomeHero } from '@/components/home/hero'
import { HomePrinciples } from '@/components/home/principles'
import { HomePillars } from '@/components/home/pillars-section'
import { HomeLatest } from '@/components/home/latest-posts'
import { HomeNewsletter } from '@/components/home/newsletter'
import { collectionSchema } from '@/lib/structured-data'
import { siteConfig } from '@/constants/site'
import { getPillars, getFeaturedPosts, getRecentPosts } from '@/lib/queries'

interface HomeProps {
    pillars: Pillar[]
    feature: PostCard | null
    posts: PostCard[]
}

const Home: FC<HomeProps> = ({ pillars, feature, posts }): ReactNode => {
    const listed = [feature, ...posts].filter((post): post is PostCard => Boolean(post))

    const collection = collectionSchema({
        name: `${siteConfig.name}: Whole-Person Wellness`,
        description: siteConfig.description,
        path: '/',
        posts: listed
    })

    return (
        <Layout>
            <Seo canonical='/' jsonLd={collection} />

            <HomeHero />
            <HomePrinciples />
            <HomePillars pillars={pillars} />
            <HomeLatest feature={feature} posts={posts} />
            <HomeNewsletter />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [pillars, featured, recent] = await Promise.all([
        getPillars(),
        getFeaturedPosts(1),
        getRecentPosts(7)
    ])

    const feature = featured[0] ?? recent[0] ?? null
    const posts = recent.filter((post) => post.id !== feature?.id).slice(0, 6)

    return {
        props: { pillars, feature, posts },
        revalidate: 300
    }
}

export default Home
